import { Button } from '@/components/ui/button';
import type { FormEvent, KeyboardEvent } from 'react';

interface ChatComposerProps {
  /** The draft, owned above and persisted in the browser (§8). */
  value: string;
  onChange: (value: string) => void;
  /** Queues the message. It leaves now or at the reconnection - not this component's business. */
  onSend: () => void;
}

/**
 * The composer of S3 and S4 (chat spec §8, §14).
 *
 * **Enter sends, Shift+Enter breaks a line** - the same convention as
 * `session/chat-input.tsx`, and that agreement is the point (issue #18).
 *
 * This used to be the INVERSE, and §14 recorded the divergence knowingly: in an
 * interview, a half-composed thought sent by reflex was judged to cost more
 * than one extra click. What that argument left out is the person: a tester
 * under `/s/<jeton>` and a guest under `/chat/<jeton>` were being taught
 * opposite reflexes inside the same product, and some people did both. §14 had
 * named its own remedy for the day the divergence became a nuisance - « la
 * correction est d'unifier les deux composeurs, pas de retoucher celui-ci
 * seul » - and the side that survives is the one whose comment says its
 * friction is what makes testers drop out of a fifteen-minute conversation. So
 * this composer moved, and the other one did not.
 *
 * The half-composed thought is still a real cost; it is now paid by
 * Shift+Enter, the convention people already carry in from every other chat.
 *
 * Two decisions from before are unchanged, and neither is re-litigated here.
 *
 * **It is never inert.** No `disabled`, no `readOnly`, not even while the
 * connection is down: that is the whole point of choosing a client-side queue
 * over a blocked field (§14). She keeps writing through a deploy, and the queue
 * carries it. The Send button follows - it is dark only on an empty draft,
 * because there is nothing to queue, and never because the network is away.
 *
 * **It says nothing about length.** §14 decided there is no product ceiling;
 * the transport's bound (`CHAT_MESSAGE_MAX_CHARS`) is a safety bound and is
 * deliberately absent from this component - no counter, no `maxLength`, nothing
 * an écrivain could read as « make it shorter ».
 *
 * There is no hint line under the field either, and its absence is a decision
 * rather than an oversight. It existed to teach the convention that was the
 * inverse of the other composer's - the very thing this component no longer
 * does - and it lived in the field's own column, which pushed the `textarea` up
 * and left the Send button hanging above the bottom of the box it belongs to.
 * The promise its second half carried (« brouillon conservé, même après un
 * rafraîchissement ») is still true, and still kept by `chat-outbox.ts`; it
 * never needed a sentence to be true.
 */
export function ChatComposer({ value, onChange, onSend }: ChatComposerProps) {
  function send() {
    if (value.trim() === '') return;
    onSend();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    send();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Without this the newline lands in the draft as well as sending it, and
      // the field she gets back has a blank first line in it.
      event.preventDefault();
      send();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2 pb-2" data-testid="chat-composer">
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        rows={2}
        placeholder="Écrivez votre réponse…"
        aria-label="Votre message"
        className="min-h-16 flex-1 resize-none rounded-md border border-gray-200 bg-blanc px-3 py-2 text-base shadow-md transition-colors duration-150 focus:border-bleu-nuit focus:outline-none"
      />
      <Button type="submit" disabled={value.trim() === ''}>
        Envoyer
      </Button>
    </form>
  );
}
